import { useEffect, useRef } from 'react';
import { loadImageToCanvas } from '../utils/loadImageToCanvas';
import useLabelingStore from '../store/useDetectionStore';
import { applyStrokeScale, applyViewport, getViewportState } from '../utils/canvasViewport';

/**
 * Repaints the canvas when the selected image (or its annotation arrays) change.
 *
 * Switching image fits the new one to the panel; a repaint caused by an
 * annotation change (AI suggestions, undo/redo) keeps the zoom and pan the user
 * had, so the view never jumps out from under them mid-task.
 */
const useImageLoader = (
  images,
  currentImageIndex,
  canvas,
  setIsDrawingPolygon,
  setPolygonPoints,
  setPolygonObj,
  setError,
  classes,
  BASE_STROKE_WIDTH
) => {
  const lastImageKeyRef = useRef(null);

  useEffect(() => {
    if (!canvas || images.length === 0) return;
    const image = images[currentImageIndex];
    if (!image) return;

    const key = image.id ?? image.fileName ?? currentImageIndex;
    const isNewImage = lastImageKeyRef.current !== key;
    lastImageKeyRef.current = key;

    const preserved = getViewportState(canvas);
    const { showFill } = useLabelingStore.getState();

    loadImageToCanvas(image, currentImageIndex, canvas, setError, classes, BASE_STROKE_WIDTH, {
      zoom: isNewImage ? 1 : preserved.zoom,
      showFill,
      onLoaded: ({ width, height }) => {
        useLabelingStore.getState().ensureImageDimensions(currentImageIndex, width, height);
        if (isNewImage) {
          useLabelingStore.getState().zoomToFit();
        } else {
          applyStrokeScale(canvas, preserved.zoom, BASE_STROKE_WIDTH);
          applyViewport(canvas, preserved.zoom, preserved.x, preserved.y, image);
        }
      },
    });

    setIsDrawingPolygon(false);
    setPolygonPoints([]);
    setPolygonObj(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, currentImageIndex, images]);
};

export default useImageLoader;
